// eslint-disable-next-line @nx/enforce-module-boundaries
import { getUser, getWeaveSessionId, isWeaveUser } from '@frontend/auth-helpers';
import { sharedIdleDetector } from './idleDetectorInstance';

type FlowState = 'init' | 'in-progress' | 'completed';
type FlowStatus = null | 'success' | 'fail' | 'abandoned';

type Primitive = string | number | boolean | null | undefined;

type FlowEvent = {
  step: string;
  timestamp: number;
  context?: Record<string, Primitive>;
  /** condense: true is magic that tells the flow to condense consecutive events with the same step name into one before reporting */
  options?: { condense?: boolean };
};

type FlowCompleteEventName = 'flow-succeeded' | 'flow-failed' | 'flow-abandoned';

// common data we want to track for every flow
type BaseFlowMetadata = {
  userId: string; // ID of the user performing the flow
  sessionId: string; // Correlate flows within a user session
  userAgent: string; // browser user agent
  screenHeight: number;
  screenWidth: number;
  flags: string[]; // feature flag identifiers active for the flow
};

export type FlowConfig = Partial<FlowConfigInternal>;

export type FlowConfigInternal = {
  /** is debug logging enabled */
  debug: boolean;
  /** is reporting enabled */
  report: boolean;
  /** minimum time threshold to report the flow (default: 3000ms) */
  reportThresholdMs: number;
  /** if true, flows with no events will not be reported */
  filterOutEmptyFlows: boolean;
  /** callback when flow is completed */
  onComplete?: (flow: Flow) => void;
  /** whether to flush Pendo events on flow completion, helpful if you're about to navigate away */
  flushPendoOnComplete?: boolean;
  /** whether to include Weave users in the flow reporting, excluded by default */
  includeWeaveUsers?: boolean;
  /** fraction of flows to report, 0–1 (default: 1 = 100%). Useful to throttle high-volume flows */
  sampleRate?: number;
};

const isLocalDev =
  typeof window !== 'undefined' && window.location.hostname === 'localhost' && import.meta.env.MODE === 'development';

export class Flow<ExtraMetadata extends object = object> {
  readonly id = crypto.randomUUID();
  readonly name: string;
  private state: FlowState = 'init';
  private startedAt: number | null = null;
  private endedAt: number | null = null;
  private events: FlowEvent[] = [];
  private metadata: BaseFlowMetadata & ExtraMetadata;
  private reported = false;
  private config: FlowConfigInternal;

  // Idle time tracking
  private idleTimeMs = 0;
  private idleStartedAt: number | null = null;
  private unsubscribeIdle: (() => void) | null = null;

  constructor(name: string, initialMetadata?: Partial<ExtraMetadata>, flowConfig?: FlowConfig) {
    this.name = name;
    this.config = {
      debug: flowConfig?.debug ?? isLocalDev ? true : false,
      report: flowConfig?.report ?? isLocalDev ? false : true,
      reportThresholdMs: flowConfig?.reportThresholdMs ?? 3000,
      filterOutEmptyFlows: flowConfig?.filterOutEmptyFlows ?? true,
      includeWeaveUsers: flowConfig?.includeWeaveUsers ?? false,
      flushPendoOnComplete: flowConfig?.flushPendoOnComplete ?? false,
      sampleRate: flowConfig?.sampleRate ?? 1,
      ...flowConfig,
    };
    // Extract flags before setting metadata
    const { flags: initialFlags, ...restMetadata } = (initialMetadata || {}) as any;

    this.metadata = {
      userId: getUser()?.userID ?? '',
      sessionId: getWeaveSessionId() ?? '',
      userAgent: navigator.userAgent ?? 'unknown',
      screenHeight: window.innerHeight ?? -1,
      screenWidth: window.innerWidth ?? -1,
      flags: [],
      ...restMetadata, // Don't include flags here
    } as BaseFlowMetadata & ExtraMetadata;

    // Then add flags using the proper method
    if (Array.isArray(initialFlags)) {
      this.addFlags(...initialFlags);
    }
  }

  static create<TExtraMetadata extends object = object>(
    name: string,
    initialMetadata?: Partial<TExtraMetadata>,
    flowConfig?: FlowConfig
  ) {
    return new Flow<TExtraMetadata>(name, initialMetadata, flowConfig);
  }

  static start<TExtraMetadata extends object = object>(
    name: string,
    initialMetadata?: Partial<TExtraMetadata>,
    flowConfig?: FlowConfig
  ) {
    const flow = new Flow<TExtraMetadata>(name, initialMetadata, flowConfig);
    flow.start();
    return flow;
  }

  start(startTime?: number) {
    if (this.state !== 'init') return this;
    this.state = 'in-progress';
    this.startedAt = startTime ?? Date.now();
    this.step('flow-start');

    // Subscribe to idle detection
    this.unsubscribeIdle = sharedIdleDetector.onChange((idleState) => {
      if (idleState === 'idle' && this.idleStartedAt === null) {
        // Only record idle start if not already idle (ignore duplicate idle emissions)
        this.idleStartedAt = Date.now();
      } else if (idleState === 'active' && this.idleStartedAt !== null) {
        this.idleTimeMs += Date.now() - this.idleStartedAt;
        this.idleStartedAt = null;
      }
    });

    return this;
  }

  /** Log a step in the flow (for multi-step tracking). Add condense: true to context to condense consecutive steps with the same name */
  step(stepName: string, context?: FlowEvent['context'], options?: { condense?: boolean }) {
    if (this.state !== 'in-progress') this.start(); // auto-start if not already started
    this.events.push({ step: stepName, timestamp: Date.now(), context, options });
    if (this.config.debug) {
      console.log(`[Flow ${this.name}] step: ${stepName}`, context || '', this);
    }
    return this;
  }

  /** Add or update metadata during the flow */
  setMetadata(updates: Partial<BaseFlowMetadata & ExtraMetadata>) {
    this.metadata = { ...this.metadata, ...updates };
    return this;
  }

  /** Add feature flag identifiers that were active for the flow */
  addFlags(...flags: string[]) {
    if (!flags.length) return this;

    const sanitizedFlags = flags.map((flag) => flag?.trim()).filter((flag): flag is string => Boolean(flag?.length));

    if (!sanitizedFlags.length) return this;

    const uniqueFlags = new Set(this.metadata.flags);
    sanitizedFlags.forEach((flag) => uniqueFlags.add(flag));
    this.metadata.flags = Array.from(uniqueFlags);

    return this;
  }

  /** Mark the flow as completed in some form */
  complete(status: FlowStatus, context?: FlowEvent['context']) {
    if (this.state !== 'in-progress') return this;
    this.state = 'completed';
    this.step(this.mapFlowStatusToEventName(status), { ...context });
    this.endedAt = Date.now();

    // Finalize idle time if currently idle
    if (this.idleStartedAt !== null) {
      this.idleTimeMs += Date.now() - this.idleStartedAt;
      this.idleStartedAt = null;
    }

    // Unsubscribe from idle detection
    this.unsubscribeIdle?.();
    this.unsubscribeIdle = null;

    this.report();
    this.config?.onComplete?.(this);
    return this;
  }

  /** convenience method for .complete('abandoned') */
  abandon(context?: FlowEvent['context']) {
    return this.complete('abandoned', context);
  }

  /** convenience method for .complete('success') */
  success(context?: FlowEvent['context']) {
    return this.complete('success', context);
  }

  /** convenience method for .complete('fail') */
  fail(context?: FlowEvent['context']) {
    return this.complete('fail', context);
  }

  private mapFlowStatusToEventName(status: FlowStatus): FlowCompleteEventName {
    switch (status) {
      case 'success':
        return 'flow-succeeded';
      case 'fail':
        return 'flow-failed';
      case 'abandoned':
      default:
        return 'flow-abandoned';
    }
  }

  /** Return the total wall-clock time (ms) from start to end */
  getTotalTime(): number {
    if (this.startedAt === null || this.endedAt === null) return 0;
    return this.endedAt - this.startedAt;
  }

  /** Return the accumulated idle time (ms) */
  getIdleTime(): number {
    return this.idleTimeMs;
  }

  /** Return the active elapsed time (ms), excluding idle time */
  getElapsedTime(): number {
    return this.getTotalTime() - this.getIdleTime();
  }

  getEventCount(): number {
    return this.events.length;
  }

  isInProgress() {
    return this.state === 'in-progress';
  }

  isComplete() {
    return this.state === 'completed';
  }

  getFinalStatus() {
    if (!this.isComplete()) return null;

    return this.events.at(-1)?.step === 'flow-succeeded'
      ? 'success'
      : this.events.at(-1)?.step === 'flow-failed'
      ? 'fail'
      : this.events.at(-1)?.step === 'flow-abandoned'
      ? 'abandoned'
      : null;
  }

  getLastEvent() {
    return this.events.at(-1) || null;
  }

  /**
   * Pendo has a 1024 char limit on each cell in it's event row table, so when the events array gets too big,
   * we need to condense it by merging consecutive events with the same name that have the condense flag set.
   * This uses a 2-pointers approach and modifies this.events in place
   */
  private condenseDuplicateSteps() {
    if (this.events.length === 0) return;

    const result: FlowEvent[] = [];
    let left = 0;

    while (left < this.events.length) {
      const currentEvent = this.events[left];

      // If this event doesn't have condense flag, just add it and move on
      if (!currentEvent.options?.condense) {
        result.push(currentEvent);
        left++;
        continue;
      }

      // Find the end of consecutive events with same step name and condense flag
      let right = left;
      while (
        right < this.events.length &&
        this.events[right].step === currentEvent.step &&
        this.events[right].options?.condense
      ) {
        right++;
      }

      // Create condensed event from the group [left, right)
      const eventGroup = this.events.slice(left, right);
      result.push(this.createCondensedEvent(eventGroup));

      // Move left pointer to continue processing
      left = right;
    }

    this.events = result;
  }

  private createCondensedEvent(eventGroup: FlowEvent[]): FlowEvent {
    const firstEvent = eventGroup[0];
    const lastEvent = eventGroup[eventGroup.length - 1];

    return {
      ...firstEvent,
      timestamp: firstEvent.timestamp,
      context: {
        ...firstEvent.context,
        eventCount: eventGroup.length,
        ...(eventGroup.length > 1 && { endTimestamp: lastEvent.timestamp }),
      },
    };
  }

  isEmptyFlow(): boolean {
    return this.events.filter((event) => !event.step.includes('flow-')).length === 0;
  }

  report() {
    const rawRate = this.config.sampleRate ?? 1;
    const sampleRate = !isFinite(rawRate) || isNaN(rawRate) ? 1 : Math.min(1, Math.max(0, rawRate));
    if (this.config.debug && sampleRate !== rawRate) {
      console.warn(`[Flow ${this.name}] sampleRate "${rawRate}" is out of range, defaulting to ${sampleRate}.`);
    }
    if (
      this.reported ||
      this.getElapsedTime() < this.config.reportThresholdMs ||
      !this.config.report ||
      (this.config.filterOutEmptyFlows && this.isEmptyFlow()) ||
      (isWeaveUser() && !this.config.includeWeaveUsers) ||
      Math.random() >= sampleRate
    ) {
      if (this.config.debug) {
        console.log(`[Flow ${this.name}] skipping report.`);
      }
      return;
    }
    if (this.state !== 'completed') {
      console.warn(`Flow ${this.name} is not completed. Only completed flows can be reported.`);
      return;
    }

    // Condense duplicate steps before reporting
    this.condenseDuplicateSteps();

    this.reported = true;

    // in the future, we'll send elsewhere but using Pendo for now
    this.sendToPendo();
  }

  sendToPendo() {
    const pendoEventName = `flow.${this.name}`;
    // don't report options
    const events = this.events.map((e) => {
      const { options, ...rest } = e;
      return rest;
    });
    const pendoEvent = {
      ...this.metadata,
      flowId: this.id,
      flowName: this.name,
      status: this.getFinalStatus(),
      startedAt: this.startedAt,
      endedAt: this.endedAt,
      totalTimeMs: this.getTotalTime(),
      elapsedTimeMs: this.getElapsedTime(),
      idleTimeMs: this.getIdleTime(),
      events: JSON.stringify(events),
      userAgent: this.metadata.userAgent, // report UA to Pendo because they give you a pre-parsed one that is often wrong
      screenHeight: this.metadata.screenHeight,
      screenWidth: this.metadata.screenWidth,
      flags: [...this.metadata.flags],
    };
    if (this.config.debug) {
      console.log(`[Flow ${this.name}] reporting to pendo:`, pendoEvent);
    }
    if (this.config.report) {
      if ((globalThis as any).pendo) {
        (globalThis as any).pendo.track(pendoEventName, pendoEvent as any);
        if (this.config.flushPendoOnComplete) {
          (globalThis as any).pendo.flushNow();
        }
      }
    } else if (this.config.debug) {
      console.log(`[Flow ${this.name}] reporting disabled, not sending`);
    }
  }
}
